import {
  type DetailedOrganizationRes,
  type HeadlineResponseOrganizationResponseRelatedPagedData,
  OrganizationType,
  type OrgTypeClassHierarchyResPagedData,
  rdbV1OrganizationsDetail,
  rdbV1OrganizationsOrgTypeClassHierarchiesDetail,
  rdbV1OrganizationsRosterDetail,
  type RdbV1OrganizationsRosterDetailParams,
  rdbV2OrganizationsAllTimeCommitsDetail,
  type RosterCompResAllTimeCommitsRelatedResRelatedPagedData,
  type RosterItemResponseOrganizationRosterResponseRelatedPagedData,
  type ScoreboardResponseRelatedScheduleResponseRelatedPagedData,
  type SingleTeamRankingResponse,
  v1OrganizationsHeadlinesDetail,
  v1TeamRankingsRankingsDetail,
  type V1TeamRankingsRankingsDetailParams,
  v2OrganizationsScheduleDetail,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/index';
import { getFulfilledValue } from '@on3/ui-lib/utils/api';
import { getAssetUrl } from '@on3/ui-lib/utils/images';
import { getIdFromParam, getRankingSportKey } from '@on3/ui-lib/utils/url';
import { type AxiosError } from 'axios';
import { HighSchoolPage } from 'components/HighSchool/HighSchoolPage';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { type GetServerSideProps } from 'next';
import { GraphSchema } from 'store/schemas/Graph';
import { ScheduleSchema } from 'store/schemas/Schedule';
import { SportOrganizationSchema } from 'store/schemas/SportOrganization';
import { createCanonicalUrl } from 'utils/meta';

interface IHighSchoolOrgPageProps extends IGenericPageProps {
  pageParams: {
    sport: string;
  };
  orgInfo: DetailedOrganizationRes;
  formerAthletes?: RosterCompResAllTimeCommitsRelatedResRelatedPagedData;
  currentAthletes?: RosterItemResponseOrganizationRosterResponseRelatedPagedData;
  highSchoolArticles?: HeadlineResponseOrganizationResponseRelatedPagedData;
  schedule?: ScoreboardResponseRelatedScheduleResponseRelatedPagedData;
  rankings: SingleTeamRankingResponse | null;
  classification: OrgTypeClassHierarchyResPagedData | null;
}

const HighSchoolOrgPage = ({
  pageParams,
  siteData,
  orgInfo,
  formerAthletes,
  highSchoolArticles,
  currentAthletes,
  schedule,
  rankings,
  classification,
  ...props
}: IHighSchoolOrgPageProps) => {
  const title = `${orgInfo?.name} (${orgInfo?.city}) - Football, Basketball: Scores, Schedules, Rankings, Recruiting and News`;
  const description = `The latest ${title} info.`;
  const noIndex =
    formerAthletes?.pagination?.count === 0 &&
    currentAthletes?.pagination?.count === 0 &&
    schedule?.pagination?.count === 0 &&
    highSchoolArticles?.pagination?.count === 0;
  const { slug, key } = orgInfo || {};
  const urlSlug = `${slug}-${key}`;
  const canonical = createCanonicalUrl(`/high-school/${urlSlug}/`);

  const schema = new GraphSchema({
    items: [
      new SportOrganizationSchema({
        name: orgInfo.name as string,
        url: canonical,
        sport: pageParams.sport,
        logo: getAssetUrl(orgInfo.defaultAsset),
        mascot: orgInfo.mascot,
        location: {
          address: orgInfo.address,
          city: (orgInfo.city || '').split(', ')[0],
          state: (orgInfo.city || '').split(', ')[1],
          zipCode: orgInfo.zipCode,
          country: 'US',
        },
      }).toJSON(),
      new ScheduleSchema({
        name: orgInfo.name as string,
        url: canonical,
        sport: pageParams.sport,
        event: schedule?.list || [],
      }).toJSON(),
    ],
  }).toJSON();

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      noIndex={noIndex}
      schema={schema}
      siteData={siteData}
      title={title}
    >
      <HighSchoolPage
        classification={classification}
        currentAthletes={currentAthletes}
        formerAthletes={formerAthletes}
        highSchoolArticles={highSchoolArticles}
        orgInfo={orgInfo}
        rankings={rankings}
        schedule={schedule}
      />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const highSchoolSiteKey = 402;
  const siteData = await withValidUrl.isValid(ctx, highSchoolSiteKey);
  const userData = await withAuthentication.isAuth(ctx);

  const {
    year,
    sport = 'football',
    id,
  } = ctx.query as { id: string; sport: string; year: string };

  const rankingSports = siteData?.rankingSports ?? [];

  const orgKey = getIdFromParam(id);
  const sportKey = getRankingSportKey(sport, rankingSports);

  if (!siteData || !orgKey || !sportKey) {
    return {
      notFound: true,
    };
  }

  const paramYear = year
    ? Number(year)
    : Number(siteData.defaultYears.highSchoolYear);

  try {
    const [orgInfo] = await Promise.allSettled([
      rdbV1OrganizationsDetail(proxyApi, orgKey),
    ]);
    const orgInfoData = getFulfilledValue(orgInfo);

    const orgSlug = `${orgInfoData?.slug}-${orgInfoData?.key}`;
    const orgType = orgInfoData?.orgType;

    if (orgType !== OrganizationType.HighSchool) {
      return { notFound: true };
    }

    if (id !== orgSlug) {
      return {
        redirect: {
          permanent: true,
          destination: `/high-school/${orgSlug}/`,
        },
      };
    }

    const commitParams = {
      sportKey,
      year: paramYear,
      organizationKey: orgKey,
    };
    const headlineParams = {
      orgKey,
      useTag: true,
    };
    const rosterParams: RdbV1OrganizationsRosterDetailParams = {
      sportKey,
      year: paramYear,
      organizationKey: orgKey,
    };
    const scheduleParams = {
      orgKey,
      sportKey,
      year: paramYear,
    };
    const rankingParams: V1TeamRankingsRankingsDetailParams = {
      orgKey,
      sportKey,
      year: paramYear,
    };
    const classificationParams = {
      organizationKey: orgKey,
      sportKey,
      year: paramYear,
    };
    const [
      formerAthletes,
      highSchoolArticles,
      currentAthletes,
      schedule,
      rankings,
      classification,
    ] = await Promise.allSettled([
      rdbV2OrganizationsAllTimeCommitsDetail(proxyApi, commitParams),
      v1OrganizationsHeadlinesDetail(proxyApi, headlineParams),
      rdbV1OrganizationsRosterDetail(proxyApi, rosterParams),
      v2OrganizationsScheduleDetail(proxyApi, { ...scheduleParams }),
      v1TeamRankingsRankingsDetail(proxyApi, rankingParams),
      rdbV1OrganizationsOrgTypeClassHierarchiesDetail(
        proxyApi,
        classificationParams,
      ),
    ]);

    const data = {
      pageType: 'database',
      siteData,
      userData: userData || [],
      orgInfo: orgInfoData,
      formerAthletes: getFulfilledValue(formerAthletes),
      highSchoolArticles: getFulfilledValue(highSchoolArticles),
      currentAthletes: getFulfilledValue(currentAthletes),
      schedule: getFulfilledValue(schedule),
      rankings: getFulfilledValue(rankings),
      classification: getFulfilledValue(classification),
      pageParams: {
        sport,
      },
    };

    return { props: { ...data } };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.error(err);

    return { props: {} };
  }
};

export default withValidUrl(HighSchoolOrgPage);
